import type { ButtonHTMLAttributes, ReactNode } from 'react'
import * as stylex from '@stylexjs/stylex'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode
  fullWidth?: boolean
  size?: ButtonSize
  variant?: ButtonVariant
}

const styles = stylex.create({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4375rem',
    borderRadius: '0.75rem',
    fontWeight: 600,
    transitionProperty: 'color, background-color, border-color, box-shadow, opacity',
    transitionDuration: '150ms',
    ':focus-visible': { outline: '2px solid #3f8654', outlineOffset: '2px' },
    cursor: { ':disabled': 'not-allowed' },
    opacity: { ':disabled': 0.5 },
  },
  sm: { minHeight: '44px', paddingInline: '12px', fontSize: '12px' },
  md: { minHeight: '44px', paddingInline: '14px', fontSize: '13px' },
  lg: { minHeight: '44px', paddingInline: '18px', fontSize: '14px' },
  fullWidth: { width: '100%' },
  primary: {
    backgroundColor: { default: '#408b56', ':hover': '#347c48' },
    color: '#fff',
  },
  secondary: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#dbe5d9',
    backgroundColor: { default: '#fff', ':hover': '#f2f8f1' },
    color: '#4e7057',
  },
  ghost: {
    backgroundColor: { default: 'transparent', ':hover': '#f2f8f1' },
    color: '#4e7057',
  },
  danger: {
    backgroundColor: { default: '#b84a45', ':hover': '#9f3e3a' },
    color: '#fff',
  },
})

export default function Button({
  children,
  className = '',
  fullWidth = false,
  size = 'md',
  variant = 'primary',
  type = 'button',
  ...props
}: ButtonProps) {
  const buttonProps = stylex.props(styles.root, styles[size], styles[variant], fullWidth && styles.fullWidth)

  return (
    <button {...props} className={`${buttonProps.className} ${className}`.trim()} style={{ ...buttonProps.style, ...props.style }} type={type}>
      {children}
    </button>
  )
}
